import './TableData.scss';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LuArrowLeftToLine, LuArrowRightFromLine } from 'react-icons/lu';
import { DTB_POPUP_Z } from '@srm/ui/data-table/popup-z-index';
import DataTable, { IDataTableColumn, IDataTableHandle, IDataTableRevealRowRequest } from '@srm/ui/data-table';
import Skeleton from '@srm/ui/skeleton';
import TableRowCount from '@srm/ui/table-row-count';
import Tooltip from '@srm/ui/tooltip';
import { TabsContext } from '@srm/ui/context/TabsContext';
import { TableColumnSettingsContext } from '@/context/TableColumnSettingsContext';
import { DocumentTitleContext } from '@/context/DocumentContext';
import { useDocumentTableBlockTitle } from '@/context/DocumentTableCountContext';
import { useMountOnFirstVisible } from '@srm/ui/hooks/useMountOnFirstVisible';
import { IDocumentTable } from '@models/Forms/IForms';
import {
    buildTableColumns,
    collectTableColumns,
    getTableGrouping,
    getTableSort,
    TableRow,
} from '@molecules/formbuilder/controls/Table/controls/tableColumns';
import { rowKeyOf } from '@molecules/formbuilder/controls/Table/controls/tableRuleHooks';
import { IHiddenColumnIssues } from '@molecules/formbuilder/controls/Table/controls/tableHiddenIssues';
import ColumnSetsBar from './ColumnSetsBar';
import HiddenIssuesChip from './HiddenIssuesChip';
import TableHelpButton from './TableHelpButton';
import { hiddenIssuesMessage } from './hiddenIssuesMessage';
import { scrollToColumn } from './scrollToColumn';
import { IColumnSet, useColumnSets } from './useColumnSets';
import { useHiddenIssues } from './useHiddenIssues';
import TableSchemeDebugButton from '@molecules/DebugPanel/TableSchemeDebug/TableSchemeDebugButton';
import { documentFormTableScheme } from '@molecules/DebugPanel/TableSchemeDebug/documentFormTableScheme';

export interface ITableDataProps {
    table: IDocumentTable;
    /** Опциональная пользовательская подпись таблицы; `name` остаётся техническим именем. */
    caption?: React.ReactNode;
    rowCount?: React.ReactNode;
    emptyPresentation?: React.ReactNode;
    rows: TableRow[];
    /** Обогащённые колонки (editable/render/editor) от EditTable; без них — базовый адаптер. */
    columns?: IDataTableColumn<TableRow>[];
    /** Данные ещё грузятся (таблицы-линков едут отдельным запросом) — скелетон вместо «Нет данных». */
    loading?: boolean;
    error?: unknown;
    /** Коммит ячейки — пайплайн changes→формулы→setValue у владельца формы. */
    onCellCommit?: (row: TableRow, field: string, value: unknown) => void;
    /** Программное открытие редактора ячейки (автофокус addRowFocusColumn). */
    editCellRequest?: { rowId: string; field: string; token: number } | null;
    /** Показать только что добавленную строку независимо от автофокуса ячейки. */
    revealRowRequest?: IDataTableRevealRowRequest | null;
    /** Кнопки операций (добавить строку и т.п.) в туллбаре таблицы. */
    toolbarStart?: React.ReactNode;
    /** Справка таблицы: кнопка «?» первой в туллбаре (у компактной подтаблицы с заголовком — рядом с ним). */
    help?: string;
    /** Кнопки строки (редактировать/удалить/копировать). */
    rowActions?: (row: TableRow) => React.ReactNode;
    rowActionsPosition?: 'left' | 'right';
    rowActionsAlwaysVisible?: boolean;
    rowActionsWidth?: number;
    /** Шапка колонки действий (групповое удаление выбранных строк). */
    rowActionsHeader?: React.ReactNode;
    /** Строки, отрисованные таблицей: по ним владелец решает состав служебных колонок. */
    onRenderedRowsChange?: (rows: TableRow[]) => void;
    /** Чекбоксы выбора строк (мультиудаление/мультиредактирование). */
    selectable?: boolean;
    onSelectionChange?: (keys: string[]) => void;
    /** Перетаскивание строк; порядок применяет владелец формы. */
    allowRowReordering?: boolean;
    onRowReorder?: (args: { rows: TableRow[]; targetRow: TableRow; position: 'before' | 'after' }) => void;
    /** Master-detail: подтаблицы в раскрываемой строке. */
    /** Режим просмотра: группировка берётся из groupIndexView. */
    viewMode?: boolean;
    /** Клик по экспорту (аналитика) — сам файл собирает таблица. */
    onExport?: () => void;
    /** Корешок состояния строки (ошибка ввода / незаполненные обязательные). */
    rowStatus?: (row: TableRow) => 'error' | 'warning' | undefined;
    /** Версия вида строки: правила, досчитанные владельцем по ключу строки (см. DataTable `rowVersion`). */
    rowVersion?: (row: TableRow) => string | number | undefined;
    /** Версия вида ячейки (см. DataTable `cellVersion`). */
    cellVersion?: (row: TableRow, colId: string) => string | number | undefined;
    renderDetail?: (row: TableRow) => React.ReactNode;
    canExpandRow?: (row: TableRow) => boolean;
    /** Компактный режим подтаблицы: без фильтров и с ужатым туллбаром
     *  (пустой туллбар не рендерится вовсе). */
    compact?: boolean;
    /** Туллбар и шапка колонок прилипают к верху прокрутки страницы (кроме подтаблиц). */
    stickyHeader?: boolean;
    /** Выбор видимых колонок и настроенные в конфигурации наборы — только у
     *  таблиц данных документа (view/edit table); прочим потребителям не нужны. */
    showColumnSets?: boolean;
    /** Ошибки в столбцах, спрятанных текущим выбором: считает владелец правил (EditTable),
     *  здесь только показывается. Без колбэка сигнала нет. */
    getHiddenIssues?: (hiddenFields: string[]) => Promise<IHiddenColumnIssues | null>;
    /** Счётчик неудачных проверок формы: рост эскалирует сигнал и возвращает его,
     *  даже если подсказку закрыли. */
    validationFailedEpoch?: number;
}

/** После раскрытия скрытых столбцов переводит клавиатурный фокус в первую
 * проблемную ячейку. Плашка исчезает, поэтому именно фокус и красная рамка
 * становятся явным продолжением действия «Показать». */
const focusFirstInvalidCell = (root: HTMLElement | null, fields: string[]) => {
    const invalid = fields
        .map((field) => root?.querySelector<HTMLElement>(`[data-col="${CSS.escape(field)}"] .tdn-cell--invalid`))
        .find((element): element is HTMLElement => !!element);
    const cell = invalid?.closest<HTMLElement>('[data-col]');
    if (!cell) return;

    cell.focus({ preventScroll: true });

    // При maxHeight у таблицы строки скроллятся внутри .dtb__scroll--y.
    // Доводим ячейку до видимой области, не сбивая горизонтальную прокрутку.
    const scroll = cell.closest<HTMLElement>('.dtb__scroll--y');
    if (!scroll) return;
    const cellRect = cell.getBoundingClientRect();
    const scrollRect = scroll.getBoundingClientRect();
    if (cellRect.top < scrollRect.top) scroll.scrollTop -= scrollRect.top - cellRect.top;
    else if (cellRect.bottom > scrollRect.bottom) scroll.scrollTop += cellRect.bottom - scrollRect.bottom;
};

/** Плавающая кнопка «В начало таблицы» при горизонтальной прокрутке —
 *  по scroll-событию контейнера атома. После клика превращается в
 *  «К прежней позиции»;
 *  ручная прокрутка отменяет запомненную позицию. */
const useScrollToStartButton = (rootRef: React.RefObject<HTMLDivElement | null>) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [showBack, setShowBack] = useState(false);
    const previousScrollLeft = useRef(0);
    // своя анимация вместо smooth: фиксированная длительность (браузерный smooth
    // на большой дистанции тянется секунды) и точный признак «идёт программная
    // прокрутка» — её scroll-события не сбрасывают «К прежней позиции»
    const animFrame = useRef<number | null>(null);
    const animating = useRef(false);
    const scrollElOf = () => rootRef.current?.querySelector<HTMLElement>('.dtb__scroll') ?? null;

    const stopAnimation = () => {
        if (animFrame.current !== null) cancelAnimationFrame(animFrame.current);
        animFrame.current = null;
        animating.current = false;
    };
    const animateTo = (el: HTMLElement, target: number) => {
        stopAnimation();
        const start = el.scrollLeft;
        const distance = target - start;
        const duration = 300;
        const t0 = performance.now();
        animating.current = true;
        const step = (now: number) => {
            const progress = Math.min(1, (now - t0) / duration);
            el.scrollLeft = start + distance * (1 - Math.pow(1 - progress, 3));
            if (progress < 1) {
                animFrame.current = requestAnimationFrame(step);
            } else {
                stopAnimation();
            }
        };
        animFrame.current = requestAnimationFrame(step);
    };

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        let scrollEl: HTMLElement | null = null;
        const onScroll = () => {
            const left = scrollEl?.scrollLeft ?? 0;
            setIsScrolled(left > 0);
            // ручная прокрутка отменяет запомненную позицию
            if (left > 0 && !animating.current) setShowBack(false);
        };
        // вмешательство пользователя прерывает программную анимацию
        const onUserScrollIntent = () => stopAnimation();
        // контейнер прокрутки рождается внутри атома после маунта
        const attach = () => {
            const el = root.querySelector<HTMLElement>('.dtb__scroll');
            if (!el || el === scrollEl) return;
            if (scrollEl) {
                scrollEl.removeEventListener('scroll', onScroll);
                scrollEl.removeEventListener('wheel', onUserScrollIntent);
                scrollEl.removeEventListener('touchstart', onUserScrollIntent);
            }
            scrollEl = el;
            el.addEventListener('scroll', onScroll, { passive: true });
            el.addEventListener('wheel', onUserScrollIntent, { passive: true });
            el.addEventListener('touchstart', onUserScrollIntent, { passive: true });
            onScroll();
        };
        attach();
        // общий путь мутаций (правки ячеек) — O(1): пока контейнер жив, не ищем заново
        const observer = new MutationObserver(() => {
            if (scrollEl?.isConnected) return;
            attach();
        });
        observer.observe(root, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
            stopAnimation();
            if (scrollEl) {
                scrollEl.removeEventListener('scroll', onScroll);
                scrollEl.removeEventListener('wheel', onUserScrollIntent);
                scrollEl.removeEventListener('touchstart', onUserScrollIntent);
            }
        };
    }, [rootRef]);

    const onClick = () => {
        const el = scrollElOf();
        if (!el) return;
        if (showBack) {
            animateTo(el, previousScrollLeft.current);
            setShowBack(false);
        } else {
            previousScrollLeft.current = el.scrollLeft;
            animateTo(el, 0);
            setShowBack(true);
        }
    };

    return { visible: isScrolled || showBack, showBack, onClick };
};

/**
 * Таблица форм документов на @atoms/DataTable (RHF-single-source, без ArrayStore).
 * Этап N0: read-only рендер конфигурационных колонок. Editing/операции/master-detail
 * подключаются следующими этапами (docs/tanstack-table-migration/TABLEDATA-NEXT.md).
 */
const TableData: FC<ITableDataProps> = ({
    table,
    caption,
    rowCount,
    emptyPresentation,
    rows,
    columns,
    loading,
    error,
    onCellCommit,
    editCellRequest,
    revealRowRequest,
    toolbarStart,
    help,
    rowActions,
    rowActionsPosition,
    rowActionsAlwaysVisible,
    rowActionsWidth,
    rowActionsHeader,
    onRenderedRowsChange,
    selectable,
    onSelectionChange,
    allowRowReordering,
    onRowReorder,
    onExport,
    rowStatus,
    rowVersion,
    cellVersion,
    renderDetail,
    canExpandRow,
    compact,
    stickyHeader,
    showColumnSets,
    getHiddenIssues,
    validationFailedEpoch,
    viewMode,
}) => {
    // без columns-пропа (read-only превью) колонки строятся из конфигурации
    const effectiveColumns = useMemo(() => columns ?? buildTableColumns(table), [columns, table]);

    // ----- N7: выбор и наборы колонок (в подтаблицах не показываем) -----
    const columnSelectionEnabled = !!showColumnSets && !compact;
    // Наборы из конфигурации карточки (тег columnSets): колонки перечислены
    // идентификаторами колонок, их надо перевести в id колонок грида (у колонок
    // с одним ключом id грида различаются суффиксом). Первый набор применяется
    // при первом открытии: по умолчанию это «Все поля»; явный default из
    // конфигурации — осознанный выбор внедренца и сильнее. Вычисляемый набор
    // «Все поля» присутствует всегда. Полосу наборов показываем только при
    // наличии хотя бы одного валидного набора из конфигурации: ненастроенная
    // таблица сохраняет правый выбор колонок, но не получает фиктивный
    // «Базовый», дублирующий «Все поля».
    const { builtinSets, hasConfiguredSets } = useMemo<{
        builtinSets: IColumnSet[];
        hasConfiguredSets: boolean;
    }>(() => {
        const allSet = { id: 'all', name: 'Все поля', fields: effectiveColumns.map((c) => c.field) };
        if (!table.columnSets?.length) return { builtinSets: [allSet], hasConfiguredSets: false };
        const fieldByColumnId = new Map<string, string>();
        const ambiguousIds = new Set<string>();
        collectTableColumns(table).forEach((source) => {
            const configId = 'id' in source.column ? source.column.id : undefined;
            if (!configId) return;
            if (fieldByColumnId.has(configId)) ambiguousIds.add(configId);
            fieldByColumnId.set(configId, source.columnId);
        });
        ambiguousIds.forEach((id) => fieldByColumnId.delete(id));
        const setIdCounts = new Map<string, number>();
        table.columnSets.forEach((set) => {
            const id = set.id || set.name;
            setIdCounts.set(id, (setIdCounts.get(id) ?? 0) + 1);
        });
        const configured = table.columnSets
            .filter((set) => {
                const id = set.id || set.name;
                if (!id || setIdCounts.get(id) !== 1 || id.startsWith('own-')) return false;
                if (id === 'all' || set.name.trim().toLowerCase() === 'все поля') return true;
                // Частично сломанный набор опасен: фильтрация ссылок молча обрезает таблицу.
                return !!set.columns?.length && set.columns.every((columnId) => fieldByColumnId.has(columnId));
            })
            .map((set) => ({
                id: set.id || set.name,
                name: set.name,
                fields:
                    set.id === 'all' || set.name.trim().toLowerCase() === 'все поля'
                        ? allSet.fields
                        : [
                              ...new Set(
                                  (set.columns ?? [])
                                      .map((columnId) => fieldByColumnId.get(columnId))
                                      .filter((field): field is string => !!field),
                              ),
                          ],
                isDefault: !!set.default,
            }))
            .filter((set) => set.fields.length > 0);
        if (configured.length === 0) return { builtinSets: [allSet], hasConfiguredSets: false };
        const hasDefault = configured.filter((set) => set.isDefault).length === 1;
        const ordered = hasDefault
            ? [...configured].sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
            : configured;
        const sets = ordered.map(({ isDefault: _isDefault, ...set }) => set);
        // Конфигурация может уже содержать полный набор — вторым «Все поля»
        // рядом с ним полоса выглядела бы дублем. Считаем таким набор с тем же
        // id/именем либо с тем же составом колонок.
        const allFields = new Set(allSet.fields);
        const existingAll = sets.find(
            (set) =>
                set.id === allSet.id ||
                set.name.trim().toLowerCase() === allSet.name.toLowerCase() ||
                (set.fields.length === allFields.size && set.fields.every((field) => allFields.has(field))),
        );
        if (existingAll) {
            // без явного default стартуем с полного набора — поднимаем его первым
            return {
                builtinSets: hasDefault ? sets : [existingAll, ...sets.filter((set) => set !== existingAll)],
                hasConfiguredSets: true,
            };
        }
        return {
            builtinSets: hasDefault ? [...sets, allSet] : [allSet, ...sets],
            hasConfiguredSets: true,
        };
    }, [effectiveColumns, table]);
    const settingsKey = useContext(TableColumnSettingsContext)(table);
    const schemaIdentity = useMemo(
        () =>
            JSON.stringify(
                collectTableColumns(table)
                    .map(({ columnId, column, kind }) => [columnId, column.id ?? '', column.key, kind])
                    .sort(([a], [b]) => a.localeCompare(b)),
            ),
        [table],
    );
    const columnSets = useColumnSets(
        settingsKey,
        effectiveColumns as never,
        builtinSets,
        columnSelectionEnabled,
        schemaIdentity,
    );
    const [navigationColumn, setNavigationColumn] = useState<string>();
    useEffect(() => {
        setNavigationColumn(undefined);
    }, [columnSets.columnVisibility]);
    // Один token — одна навигация: пересборка columns (чекбокс выделения, смена viewMode)
    // не должна заново форсить колонку и скроллить по устаревшему запросу.
    const handledNavigationToken = useRef<number>(undefined);
    useEffect(() => {
        const field = revealRowRequest?.field;
        if (!field || !effectiveColumns.some((column) => column.field === field && column.visible !== false)) return;
        if (handledNavigationToken.current === revealRowRequest.token) return;
        handledNavigationToken.current = revealRowRequest.token;
        // Без управления колонками временная видимость не нужна, а controlled-режим
        // DataTable отключил бы его собственную карту видимости (col.visible).
        if (columnSelectionEnabled) setNavigationColumn(field);
        const frame = requestAnimationFrame(() => scrollToColumn(rootRef.current, field));
        return () => cancelAnimationFrame(frame);
    }, [revealRowRequest, effectiveColumns, columnSelectionEnabled]);
    // Навигационный показ не перезаписывает сохранённый набор колонок.
    const effectiveColumnVisibility = useMemo(
        () =>
            navigationColumn
                ? { ...columnSets.columnVisibility, [navigationColumn]: true }
                : columnSets.columnVisibility,
        [navigationColumn, columnSets.columnVisibility],
    );
    // колонка, скрытая формулой visibilityRules, недоступна для выбора: её нет
    // ни в гриде, ни в чузере (набор её «помнит» — вернётся, когда правило пустит)
    const gridColumns = useMemo(
        () => (columnSelectionEnabled ? effectiveColumns.filter((c) => c.visible !== false) : effectiveColumns),
        [columnSelectionEnabled, effectiveColumns],
    );
    const availableCount = gridColumns.length;
    const visibleCount = columnSelectionEnabled
        ? gridColumns.filter((c) => effectiveColumnVisibility?.[c.field] !== false).length
        : availableCount;
    const defaultGrouping = useMemo(() => getTableGrouping(table, viewMode), [table, viewMode]);
    const defaultSort = useMemo(() => getTableSort(table), [table]);
    // ключ строки: '|NUM' либо общий суррогат для безключевых — ЕДИНОЕ
    // пространство ключей с rowKeyOf, которым EditTable ищет выбранные строки
    const rowKey = rowKeyOf;
    const rootRef = useRef<HTMLDivElement>(null);
    // Таблицы неактивных вкладок формы не монтируются до первой активации:
    // Tabs держит неактивные панели в display:none, и жадный маунт всех гридов
    // (сотни строк, прекэш формул на строку) блокировал открытие карточки.
    const tabsContext = useContext(TabsContext);
    const { containerRef, shouldMount } = useMountOnFirstVisible<HTMLDivElement>(tabsContext.activeTab);
    const scrollButton = useScrollToStartButton(rootRef);
    // Вкладку сменили мимо слоя (история браузера, якорь #page-…): панель уходит в display:none
    // вместе с развёрнутой таблицей — выходим из режима, чтобы по возвращении не встретил слой.
    const tableControlRef = useRef<IDataTableHandle<TableRow>>(null);
    useEffect(() => {
        tableControlRef.current?.exitFullscreen();
    }, [tabsContext.activeTab]);

    // ----- Ошибки в столбцах, спрятанных текущим выбором -----
    // Валидация документа выбора колонок не знает и проверяет такие столбцы наравне с
    // видимыми: без сигнала пользователь видит красные корешки строк, ни одной
    // красной ячейки и молча заблокированное сохранение.
    const hiddenFields = useMemo(
        () =>
            columnSelectionEnabled
                ? gridColumns.filter((c) => effectiveColumnVisibility?.[c.field] === false).map((c) => c.field)
                : [],
        [columnSelectionEnabled, gridColumns, effectiveColumnVisibility],
    );
    const failedEpoch = validationFailedEpoch ?? 0;
    const issues = useHiddenIssues(getHiddenIssues, hiddenFields, rows, failedEpoch);
    // Закрытие крестиком помнит, на какой попытке сохранения его закрыли:
    // следующий отказ показывает сообщение снова, «навсегда» его не спрятать.
    const [dismissedEpoch, setDismissedEpoch] = useState<number | null>(null);
    const [revealed, setRevealed] = useState<string[]>([]);
    const issuesMessage =
        columnSelectionEnabled && !viewMode && dismissedEpoch !== failedEpoch
            ? hiddenIssuesMessage({
                  issues,
                  setName: columnSets.activeSet?.name,
                  dirty: columnSets.dirty,
                  failed: failedEpoch > 0,
              })
            : null;

    const revealHiddenColumns = () => {
        const fields = issues?.columns.map((column) => column.columnId) ?? [];
        if (fields.length === 0) return;
        columnSets.revealFields(fields);
        setRevealed(fields);
        // Открытые столбцы могли оказаться далеко справа. После перерендера
        // показываем первый и ставим фокус в первую красную ячейку.
        requestAnimationFrame(() => {
            scrollToColumn(rootRef.current, fields[0]);
            focusFirstInvalidCell(rootRef.current, fields);
        });
    };

    // кнопка живёт в туллбаре грида (слот toolbarEnd): ничего не перекрывает,
    // места не занимает — появляется только при горизонтальной прокрутке
    const setsBar =
        columnSelectionEnabled && hasConfiguredSets ? (
            <ColumnSetsBar
                sets={columnSets.sets}
                activeId={columnSets.activeId}
                activeSet={columnSets.activeSet}
                dirty={columnSets.dirty}
                alert={!!issues?.columns.length}
                onApply={columnSets.applySet}
                onReset={columnSets.resetSet}
                onSaveAsNew={columnSets.saveAsNew}
                onUpdateActive={columnSets.updateActive}
                onRenameActive={columnSets.renameActive}
                onDeleteActive={columnSets.deleteActive}
            />
        ) : null;
    // Сигнал стоит между наборами и выбором столбцов — там же, где чинится:
    // отдельной полосой над гридом он двигал бы всю таблицу вниз.
    const issuesChip = issuesMessage ? (
        <HiddenIssuesChip
            message={issuesMessage}
            count={issues?.rowCount || undefined}
            onReveal={issues?.columns.length ? revealHiddenColumns : undefined}
            onDismiss={() => setDismissedEpoch(failedEpoch)}
        />
    ) : null;
    // у компактной подтаблицы туллбар есть только при операциях — справка встаёт к заголовку, если он есть
    const helpInCaption = !!help && compact && !!(caption || rowCount);
    const helpButton = help ? (
        <TableHelpButton
            help={help}
            tableName={typeof caption === 'string' ? caption : table.name}
            placement={helpInCaption ? 'caption' : 'toolbar'}
        />
    ) : null;
    const captionNode =
        caption || rowCount ? (
            <div className="table-data-next__title-group">
                {caption && (
                    <h3 className="table-data-next__caption" title={typeof caption === 'string' ? caption : undefined}>
                        {caption}
                    </h3>
                )}
                {rowCount}
                {helpInCaption && helpButton}
            </div>
        ) : null;
    // у таблицы без своего заголовка имя несёт блок формы — в шапке режима показываем его
    const blockTitle = useDocumentTableBlockTitle();
    const documentTitle = useContext(DocumentTitleContext);
    const fullscreenTitle =
        captionNode ??
        (blockTitle ? (
            <div className="table-data-next__title-group">
                <h3 className="table-data-next__caption">{blockTitle}</h3>
                <TableRowCount count={rows.length} label={table.rowCount?.label} />
            </div>
        ) : undefined);
    // справка — первой в служебной группе справа: перед жучком, фильтром, колонками и полноэкранным режимом
    const toolbarHelp = helpInCaption ? null : helpButton;
    // наборы — элемент туллбара грида (после кнопок операций), не отдельная полоса
    const toolbarWithSets =
        setsBar || issuesChip ? (
            <>
                {toolbarStart}
                {setsBar}
                {issuesChip}
            </>
        ) : (
            toolbarStart
        );

    const scrollButtonLabel = scrollButton.showBack ? 'К прежней позиции' : 'В начало таблицы';
    const formScheme = useMemo(() => documentFormTableScheme(table), [table]);
    const scrollToStartButton = scrollButton.visible ? (
        <Tooltip wrapTarget={false} zIndex={DTB_POPUP_Z} label={scrollButtonLabel}>
            <button
                type="button"
                className="dtb__icon-btn tdn-scrollstart"
                aria-label={scrollButtonLabel}
                onClick={scrollButton.onClick}
            >
                {scrollButton.showBack ? <LuArrowRightFromLine aria-hidden /> : <LuArrowLeftToLine aria-hidden />}
            </button>
        </Tooltip>
    ) : undefined;

    return (
        <div
            className={compact ? 'table-data-next table-data-next--compact' : 'table-data-next'}
            ref={(el) => {
                rootRef.current = el;
                containerRef.current = el;
            }}
            data-testid={`table-next-${table.id ?? table.key}`}
        >
            {!shouldMount && <Skeleton height={120} />}
            {shouldMount && captionNode}
            {shouldMount && (
                <DataTable
                    columns={gridColumns}
                    data={rows}
                    loading={loading}
                    error={error}
                    emptyPresentation={emptyPresentation}
                    getRowId={rowKey}
                    defaultGrouping={defaultGrouping.length ? defaultGrouping : undefined}
                    defaultSort={defaultSort.length ? defaultSort : undefined}
                    showHeaderFilter={!compact}
                    stickyHeader={stickyHeader && !compact}
                    // строку фильтров включает конфигурация (allow-filters-row), как в легаси;
                    // в подтаблицах фильтров нет вовсе
                    filterRowToggle={!compact && !!table.allowFiltersRow}
                    // перенос текста в ячейках — из конфигурации таблицы (дефолт «Все»)
                    defaultWrapMode={table.wordWrapEnabled === false ? 'nowrap' : 'wrap'}
                    defaultPageSize={table.pageSize && table.pageSize > 0 ? table.pageSize : 20}
                    onCellCommit={onCellCommit}
                    editCellRequest={editCellRequest}
                    revealRowRequest={revealRowRequest}
                    toolbarStart={toolbarWithSets}
                    toolbarEnd={
                        <>
                            {toolbarHelp}
                            <TableSchemeDebugButton
                                {...formScheme.debug}
                                columns={formScheme.columns}
                                details={formScheme.details}
                                nested={formScheme.nested}
                                buttonClassName="dtb__icon-btn"
                            />
                            {scrollToStartButton}
                        </>
                    }
                    rowActions={rowActions}
                    rowActionsPosition={rowActionsPosition}
                    rowActionsAlwaysVisible={rowActionsAlwaysVisible}
                    rowActionsWidth={rowActionsWidth}
                    rowActionsHeader={rowActionsHeader}
                    onRenderedRowsChange={onRenderedRowsChange}
                    selectable={selectable}
                    onSelectionChange={onSelectionChange}
                    allowRowReordering={allowRowReordering}
                    onRowReorder={onRowReorder}
                    rowStatus={rowStatus}
                    rowVersion={rowVersion}
                    cellVersion={cellVersion}
                    renderDetail={renderDetail}
                    canExpandRow={canExpandRow}
                    // правый чузер управляет текущим личным выбором колонок
                    showColumnChooser={columnSelectionEnabled}
                    columnVisibility={effectiveColumnVisibility}
                    onColumnVisibilityChange={columnSelectionEnabled ? columnSets.onColumnVisibilityChange : undefined}
                    columnChooserLabel={
                        columnSelectionEnabled ? `Колонки · ${visibleCount} из ${availableCount}` : undefined
                    }
                    columnChooserAlert={!!issues?.columns.length}
                    highlightColumns={revealed}
                    exportFileName={table.allowExport ? `${table.name || 'Экспорт таблицы'}.xlsx` : undefined}
                    exportTexts={
                        table.allowExport
                            ? {
                                  allowSelected: !!table.allowExportSelectedRows,
                                  exportAll: table.exportAllText || undefined,
                                  exportSelectedRows: table.exportSelectedRowsText || undefined,
                                  exportTo: table.exportToText || undefined,
                              }
                            : undefined
                    }
                    onExport={onExport}
                    // добавленная строка остаётся в конце даже при активной сортировке
                    appendedRowsLast
                    // подтаблицы живут в раскрытой строке и в drawer (у него transform) — им режим не нужен
                    allowFullscreen={!compact && table.allowFullscreen !== false}
                    controlRef={tableControlRef}
                    fullscreenTitle={fullscreenTitle}
                    fullscreenContext={documentTitle}
                />
            )}
        </div>
    );
};

export default TableData;
